"use client";

import { useEffect, useState } from "react";
import { fetchHeardAboutUsOptions, type HeardAboutUsOption } from "@/lib/api/forms";

export const HEARD_ABOUT_US_LABEL = "How did you hear about us?";

/**
 * Spec 4.2 options from Wake Speed. Empty while loading or if the fetch
 * failed. Options a server page already fetched are used without a request.
 */
export function useHeardAboutUsOptions(preloaded?: HeardAboutUsOption[]): HeardAboutUsOption[] {
  const [options, setOptions] = useState<HeardAboutUsOption[]>(preloaded ?? []);
  useEffect(() => {
    if (preloaded?.length) return undefined;
    let active = true;
    fetchHeardAboutUsOptions().then((loaded) => {
      if (active) setOptions(loaded);
    });
    return () => {
      active = false;
    };
  }, [preloaded]);
  return options;
}

/**
 * The optional "How did you hear about us?" question (lead attribution spec
 * 6.3). Like the site's other selects, the placeholder option is the label.
 * Render it only when `options` is non-empty.
 */
export function HeardAboutUsSelect({
  options,
  value,
  className,
  onChange,
}: {
  options: HeardAboutUsOption[];
  value: string;
  className: string;
  onChange: (value: string) => void;
}) {
  return (
    <select
      name="self_reported_source"
      aria-label={HEARD_ABOUT_US_LABEL}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`${className} ${!value ? "text-muted-foreground" : ""}`}
    >
      <option value="">{HEARD_ABOUT_US_LABEL}</option>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}
